import React, { useCallback, useEffect, useState } from 'react';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import { VideoGenerationJob, VideoJobDetailResponse } from '../../services/api/videoGenerationService';
import videoGenerationService from '../../services/api/videoGenerationService';
import { describeVideoFailure, videoFailureLabel } from './videoFailure';
import { PERSON_REFERENCE_VIDEO_MODEL } from './videoModelCatalog';
import type { VideoJobResubmitOverrides } from './resubmitVideoJob';
import {
  X,
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  Video,
  AlertCircle,
  Download,
  Copy,
  Check,
} from 'lucide-react';
import { usePolling } from '../../hooks/usePolling';

interface VideoJobDetailModalProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
  onJobCancelled?: () => void;
  /**
   * 失敗したジョブを作り直す。実際の送信は一覧を持つ親が行い、成功したら
   * 新規生成と同じ経路で一覧を更新する（このモーダルはポーリングを増やさない）。
   * 渡さなければ再生成ボタンは出ない。
   */
  onResubmit?: (job: VideoGenerationJob, overrides: VideoJobResubmitOverrides) => Promise<void>;
}

const VideoJobDetailModal: React.FC<VideoJobDetailModalProps> = ({
  jobId,
  isOpen,
  onClose,
  onJobCancelled,
  onResubmit,
}) => {
  const [job, setJob] = useState<VideoGenerationJob | null>(null);
  const [video, setVideo] = useState<VideoJobDetailResponse['video']>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promptTab, setPromptTab] = useState<'original' | 'enhanced'>('original');
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState<string | null>(null);

  const loadJobDetail = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await videoGenerationService.getJobDetail(jobId);
      setJob(response.job);
      setVideo(response.video);
    } catch (err) {
      setError('ジョブの詳細を取得できませんでした');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // 進行中のジョブだけ更新を追う。404/403 などで止まり、非表示タブでは取得しない。
  const isJobActive = job?.status === 'pending' || job?.status === 'processing';
  const { run: fetchJobDetail } = usePolling(loadJobDetail, {
    enabled: isOpen && !!jobId && isJobActive,
    intervalMs: 3000,
  });

  useEffect(() => {
    if (!isOpen || !jobId) return;
    fetchJobDetail();
  }, [isOpen, jobId, fetchJobDetail, loadJobDetail]);

  const handleCancel = async () => {
    if (!job || cancelling) return;

    try {
      setCancelling(true);
      await videoGenerationService.cancelJob(job.id);
      onJobCancelled?.();
      onClose();
    } catch (err) {
      console.error('Failed to cancel job:', err);
      setError('ジョブのキャンセルに失敗しました');
    } finally {
      setCancelling(false);
    }
  };

  const handleResubmit = async (overrides: VideoJobResubmitOverrides) => {
    if (!job || !onResubmit || resubmitting) return;

    setResubmitting(true);
    setResubmitError(null);
    try {
      await onResubmit(job, overrides);
      onClose();
    } catch (err: any) {
      // 同時実行数の上限やモデル側の制約はここで初めて分かる。閉じずに出す。
      setResubmitError(
        err?.response?.data?.message || err?.message || '再生成を開始できませんでした'
      );
    } finally {
      setResubmitting(false);
    }
  };

  const handleDownload = () => {
    if (!video?.url) return;
    const link = document.createElement('a');
    link.href = video.url;
    link.download = `video_${video.id}.mp4`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'movie', video_id: video.id });
  };

  const handleCopyPrompt = () => {
    const promptText = promptTab === 'enhanced' && (job as any)?.enhanced_prompt ? (job as any).enhanced_prompt : job?.prompt;
    if (!promptText) return;
    navigator.clipboard.writeText(promptText);
    setCopiedToClipboard(true);
    setTimeout(() => setCopiedToClipboard(false), 2000);
  };

  const getStatusBadge = () => {
    if (!job) return null;

    const statusConfig = {
      pending: {
        icon: <Clock className="w-4 h-4" />,
        text: '待機中',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
      },
      processing: {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        text: '動画生成中',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
      },
      completed: {
        icon: <CheckCircle className="w-4 h-4" />,
        text: '完了',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
      },
      failed: {
        icon: <XCircle className="w-4 h-4" />,
        text: '失敗',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
      },
    };

    const config = statusConfig[job.status];
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}
      >
        {config.icon}
        {config.text}
      </span>
    );
  };

  const getAspectRatioLabel = (aspectRatio: string) => {
    switch (aspectRatio) {
      case '16:9':
        return '横長 (16:9)';
      case '9:16':
        return '縦長 (9:16)';
      case '1:1':
        return '正方形 (1:1)';
      default:
        return aspectRatio;
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">動画生成ジョブ詳細</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-130px)]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8 text-red-500">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </div>
            ) : job ? (
              <div className="space-y-6">
                {/* Status and Info */}
                <div className="flex items-center justify-between">
                  {getStatusBadge()}
                  <span className="text-sm text-gray-500">
                    {new Date(job.created_at).toLocaleString('ja-JP')}
                  </span>
                </div>

                {/* Progress for processing */}
                {job.status === 'processing' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                      <div>
                        <p className="text-sm font-medium text-blue-700">動画を生成しています...</p>
                        <p className="text-xs text-blue-600 mt-1">
                          動画生成には数分かかる場合があります。このページを離れても生成は継続されます。
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Prompt */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">プロンプト</h3>
                    <button
                      onClick={handleCopyPrompt}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    >
                      {copiedToClipboard ? (
                        <>
                          <Check className="w-3 h-3" />
                          コピーしました
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          コピー
                        </>
                      )}
                    </button>
                  </div>

                  {/* Prompt Tabs */}
                  {(job as any)?.enhanced_prompt && (
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => setPromptTab('original')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          promptTab === 'original'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        元のプロンプト
                      </button>
                      <button
                        onClick={() => setPromptTab('enhanced')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          promptTab === 'enhanced'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        最適化プロンプト
                      </button>
                    </div>
                  )}

                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap">
                    {promptTab === 'enhanced' && (job as any)?.enhanced_prompt ? (job as any).enhanced_prompt : job.prompt}
                  </div>
                </div>

                {/* Generation Info */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Video className="w-4 h-4" />
                    <span>{getAspectRatioLabel(job.aspect_ratio)}</span>
                  </div>
                </div>

                {/* Error Message */}
                {(job.status === 'failed' || job.error_message) && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                      <div className="text-sm text-red-700">
                        <div data-testid="video-job-failure-reason">
                          <p className="font-medium">{videoFailureLabel(job.error_code) ?? '失敗'}</p>
                          {/* error_message は技術情報を落とした1文で、空のことも丸められていることもある */}
                          <p className="mt-0.5">
                            {job.error_message || describeVideoFailure(job.error_code)}
                          </p>
                        </div>
                        {job.status === 'failed' && (
                          // 課金は S3 保存後の recordVideoUsage だけ。失敗では引かれない。
                          <p className="mt-2 text-xs text-red-600">クレジットは消費されていません</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Resubmit */}
                {job.status === 'failed' && onResubmit && (
                  <div className="space-y-2 pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700">同じ内容で作り直す</p>
                    <button
                      data-track="video-job-resubmit"
                      data-testid="video-job-resubmit"
                      onClick={() => handleResubmit({})}
                      disabled={resubmitting}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      同じ設定で再生成
                    </button>
                    {job.error_code === 'sensitive_reference_image' && (job.reference_images?.length ?? 0) > 0 && (
                      <button
                        data-track="video-job-resubmit-no-reference"
                        data-testid="video-job-resubmit-no-reference"
                        onClick={() => handleResubmit({ reference_images: [] })}
                        disabled={resubmitting}
                        className="w-full px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        参照画像なしで再生成
                      </button>
                    )}
                    {job.error_code === 'sensitive_reference_image' && (
                      <button
                        data-track="video-job-resubmit-minimax"
                        data-testid="video-job-resubmit-minimax"
                        onClick={() => handleResubmit({ video_model: PERSON_REFERENCE_VIDEO_MODEL })}
                        disabled={resubmitting}
                        className="w-full px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        MiniMax で再生成（人物の参照画像に対応）
                      </button>
                    )}
                    {resubmitError && (
                      <p data-testid="video-job-resubmit-error" className="text-sm text-red-600">
                        {resubmitError}
                      </p>
                    )}
                  </div>
                )}

                {/* Generated Video */}
                {video && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">生成された動画</h3>
                    <div className="bg-black rounded-lg overflow-hidden">
                      <video
                        src={video.url}
                        controls
                        className="w-full"
                        style={{
                          aspectRatio: job.aspect_ratio === '9:16' ? '9/16' : job.aspect_ratio === '1:1' ? '1/1' : '16/9',
                          maxHeight: '400px',
                        }}
                      >
                        お使いのブラウザは動画再生をサポートしていません。
                      </video>
                    </div>
                    <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span>{formatDuration(video.duration_seconds)}</span>
                        <span>{formatFileSize(video.file_size)}</span>
                      </div>
                      <button
                        onClick={handleDownload}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        ダウンロード
                      </button>
                    </div>
                  </div>
                )}

                {/* Cancel Button */}
                {job.status === 'pending' && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {cancelling ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          キャンセル中...
                        </span>
                      ) : (
                        'ジョブをキャンセル'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoJobDetailModal;
